import { useState } from 'react';
import { useI18n } from '../i18n/context';
import { useTheme } from '../theme/context';
import { ChevronLeft, Globe, Sun, Moon, Monitor, Check } from 'lucide-react';

interface SettingsProps {
  onBack: () => void;
}

export default function Settings({ onBack }: SettingsProps) {
  const { lang, setLang, t } = useI18n();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [saved, setSaved] = useState(false);

  const languages = [
    { id: 'en' as const, label: 'English', flag: '🇬🇧' },
    { id: 'fr' as const, label: 'Français', flag: '🇫🇷' },
  ];

  const themes = [
    { id: 'light' as const, label: t('settings.light'), icon: Sun },
    { id: 'dark' as const, label: t('settings.dark'), icon: Moon },
    { id: 'system' as const, label: t('settings.system'), icon: Monitor },
  ];

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <button type="button" onClick={onBack} className="text-sm flex items-center gap-1 transition-colors" style={{ color: 'var(--color-app-muted)' }}>
        <ChevronLeft className="w-4 h-4" /> {t('nav.back')}
      </button>

      <div className="glass rounded-xl p-4 flex items-center gap-3">
        <Globe className="w-5 h-5 text-ocean-500" />
        <div>
          <h2 className="text-lg font-bold" style={{ color: 'var(--color-app-text)' }}>{t('settings.title')}</h2>
          <p className="text-xs" style={{ color: 'var(--color-app-muted)' }}>{t('settings.comingSoon')}</p>
        </div>
      </div>

      {/* Language */}
      <div className="card-premium p-4 space-y-3">
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--color-app-text)' }}>
          <Globe className="w-4 h-4 text-ocean-500" />
          {t('settings.language')}
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {languages.map((l) => (
            <button type="button"
              key={l.id}
              onClick={() => setLang(l.id)}
              className="flex items-center gap-2 p-3 rounded-xl border transition-all"
              style={{
                background: lang === l.id ? 'rgba(14, 165, 233, 0.1)' : 'var(--color-app-card)',
                borderColor: lang === l.id ? 'rgba(14, 165, 233, 0.5)' : 'var(--color-app-card-border)',
                color: 'var(--color-app-text)',
              }}
            >
              <span className="text-lg">{l.flag}</span>
              <span className="text-sm font-medium">{l.label}</span>
              {lang === l.id && <Check className="w-4 h-4 text-ocean-500 ml-auto" />}
            </button>
          ))}
        </div>
      </div>

      {/* Theme */}
      <div className="card-premium p-4 space-y-3">
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--color-app-text)' }}>
          <Sun className="w-4 h-4 text-ocean-500" />
          {t('settings.theme')}
        </h3>
        <div className="grid grid-cols-3 gap-2">
          {themes.map((t) => {
            const Icon = t.icon;
            const isActive = theme === t.id;
            return (
              <button type="button"
                key={t.id}
                onClick={() => setTheme(t.id)}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all"
                style={{
                  background: isActive ? 'rgba(14, 165, 233, 0.1)' : 'var(--color-app-card)',
                  borderColor: isActive ? 'rgba(14, 165, 233, 0.5)' : 'var(--color-app-card-border)',
                  color: 'var(--color-app-text)',
                }}
              >
                <Icon className="w-5 h-5" style={{ color: isActive ? '#0ea5e9' : 'var(--color-app-muted)' }} />
                <span className="text-xs font-medium">{t.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-xs" style={{ color: 'var(--color-app-muted)' }}>
          Current: {resolvedTheme === 'dark' ? 'Dark' : 'Light'} mode
        </p>
      </div>

      <button type="button"
        onClick={handleSave}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        {saved ? <Check className="w-4 h-4" /> : null}
        {saved ? 'Saved!' : t('settings.save')}
      </button>
    </div>
  );
}
